import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { AppLanguage } from '../i18n/types';
import {
  buildMonthCalendarGrid,
  isPastCalendarDayKey,
  parseCalendarDayKey,
  shiftMonth,
  todayCalendarDayKey,
  toCalendarDayKey,
} from '../utils/assetBookingCalendar';
import styles from './BookingDatePickerField.module.css';

export type BookingDayAvailability = 'available' | 'closed' | 'fullyBooked' | 'past';

type BookingDatePickerFieldProps = {
  dayKey: string | null;
  onDayKeyChange: (dayKey: string) => void;
  language: AppLanguage;
  label: string;
  dayColumnLabel: string;
  monthColumnLabel: string;
  yearColumnLabel: string;
  openCalendarLabel: string;
  prevMonthLabel: string;
  nextMonthLabel: string;
  dayAvailability?: (dayKey: string) => BookingDayAvailability;
  disabled?: boolean;
};

const WHEEL_ITEM_HEIGHT_PX = 34;
const WHEEL_VISIBLE_ROWS = 3;

type WheelItem = { value: string; label: string };

function daysInMonth(year: number, monthIndex: number): number {
  return new Date(year, monthIndex + 1, 0).getDate();
}

function monthName(monthIndex: number, language: AppLanguage): string {
  return new Intl.DateTimeFormat(language === 'he' ? 'he-IL' : undefined, { month: 'short' }).format(
    new Date(2024, monthIndex, 1),
  );
}

function ScrollWheelColumn({
  id,
  items,
  value,
  onChange,
  ariaLabel,
  disabled = false,
}: {
  id: string;
  items: WheelItem[];
  value: string;
  onChange: (next: string) => void;
  ariaLabel: string;
  disabled?: boolean;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const scrollSyncRef = useRef(false);

  const scrollToValue = useCallback(
    (nextValue: string, behavior: ScrollBehavior = 'auto') => {
      const el = listRef.current;
      if (!el) {
        return;
      }
      const index = items.findIndex((item) => item.value === nextValue);
      if (index < 0) {
        return;
      }
      scrollSyncRef.current = true;
      const top = index * WHEEL_ITEM_HEIGHT_PX;
      if (typeof el.scrollTo === 'function') {
        el.scrollTo({ top, behavior });
      } else {
        el.scrollTop = top;
      }
      window.setTimeout(() => {
        scrollSyncRef.current = false;
      }, behavior === 'smooth' ? 220 : 0);
    },
    [items],
  );

  useEffect(() => {
    scrollToValue(value);
  }, [value, items, scrollToValue]);

  const onScroll = () => {
    if (scrollSyncRef.current) {
      return;
    }
    const el = listRef.current;
    if (!el || items.length === 0) {
      return;
    }
    const index = Math.max(0, Math.min(items.length - 1, Math.round(el.scrollTop / WHEEL_ITEM_HEIGHT_PX)));
    const next = items[index]?.value;
    if (next && next !== value) {
      onChange(next);
    }
  };

  return (
    <div className={styles.wheelWrap}>
      <div className={styles.wheelHighlight} aria-hidden />
      <div
        id={id}
        ref={listRef}
        className={styles.wheel}
        role="listbox"
        aria-label={ariaLabel}
        aria-disabled={disabled || undefined}
        onScroll={onScroll}
      >
        <div className={styles.wheelSpacer} aria-hidden />
        {items.map((item) => {
          const selected = item.value === value;
          return (
            <button
              key={item.value}
              type="button"
              role="option"
              aria-selected={selected}
              className={[styles.wheelItem, selected ? styles.wheelItemSelected : ''].filter(Boolean).join(' ')}
              disabled={disabled}
              onClick={() => {
                onChange(item.value);
                scrollToValue(item.value, 'smooth');
              }}
            >
              {item.label}
            </button>
          );
        })}
        <div className={styles.wheelSpacer} aria-hidden />
      </div>
    </div>
  );
}

export function BookingDatePickerField({
  dayKey,
  onDayKeyChange,
  language,
  label,
  dayColumnLabel,
  monthColumnLabel,
  yearColumnLabel,
  openCalendarLabel,
  prevMonthLabel,
  nextMonthLabel,
  dayAvailability,
  disabled = false,
}: BookingDatePickerFieldProps) {
  const popoverId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const todayKey = todayCalendarDayKey();
  const todayParsed = parseCalendarDayKey(todayKey)!;
  const seed = dayKey ?? todayKey;
  const seedParsed = parseCalendarDayKey(seed) ?? todayParsed;

  const [year, setYear] = useState(String(seedParsed.year));
  const [month, setMonth] = useState(String(seedParsed.monthIndex + 1).padStart(2, '0'));
  const [day, setDay] = useState(String(seedParsed.day).padStart(2, '0'));

  useEffect(() => {
    const parsed = dayKey ? parseCalendarDayKey(dayKey) : null;
    if (!parsed) {
      return;
    }
    setYear(String(parsed.year));
    setMonth(String(parsed.monthIndex + 1).padStart(2, '0'));
    setDay(String(parsed.day).padStart(2, '0'));
  }, [dayKey]);

  const seedYear = Number(seed.slice(0, 4));
  const seedMonth = Number(seed.slice(5, 7)) - 1;
  const [viewYear, setViewYear] = useState(Number.isFinite(seedYear) ? seedYear : new Date().getFullYear());
  const [viewMonthIndex, setViewMonthIndex] = useState(
    Number.isFinite(seedMonth) ? seedMonth : new Date().getMonth(),
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const maxYear = todayParsed.year + 2;

  const yearItems = useMemo(() => {
    const out: WheelItem[] = [];
    for (let y = todayParsed.year; y <= maxYear; y += 1) {
      out.push({ value: String(y), label: String(y) });
    }
    return out;
  }, [maxYear, todayParsed.year]);

  const monthItems = useMemo(() => {
    const yi = Number(year);
    const startMonth = Number.isFinite(yi) && yi === todayParsed.year ? todayParsed.monthIndex + 1 : 1;
    const out: WheelItem[] = [];
    for (let m = startMonth; m <= 12; m += 1) {
      const mi = m - 1;
      const maxDay = daysInMonth(yi, mi);
      let startDay = 1;
      if (yi === todayParsed.year && mi === todayParsed.monthIndex) {
        startDay = todayParsed.day;
      }
      let hasAvailable = !dayAvailability;
      if (dayAvailability) {
        for (let d = startDay; d <= maxDay; d += 1) {
          const key = toCalendarDayKey(yi, mi, d);
          if (dayAvailability(key) === 'available') {
            hasAvailable = true;
            break;
          }
        }
      }
      if (!hasAvailable) {
        continue;
      }
      const value = String(m).padStart(2, '0');
      out.push({ value, label: monthName(m - 1, language) });
    }
    return out;
  }, [dayAvailability, language, todayParsed.day, todayParsed.monthIndex, todayParsed.year, year]);

  const dayItems = useMemo(() => {
    const yi = Number(year);
    const mi = Number(month) - 1;
    if (!Number.isFinite(yi) || !Number.isFinite(mi) || mi < 0 || mi > 11) {
      return [];
    }
    const maxDay = daysInMonth(yi, mi);
    let startDay = 1;
    if (yi === todayParsed.year && mi === todayParsed.monthIndex) {
      startDay = todayParsed.day;
    }
    const out: WheelItem[] = [];
    for (let d = startDay; d <= maxDay; d += 1) {
      const value = String(d).padStart(2, '0');
      const key = toCalendarDayKey(yi, mi, d);
      const status = dayAvailability ? dayAvailability(key) : 'available';
      if (status === 'available') {
        out.push({ value, label: value });
      }
    }
    return out;
  }, [dayAvailability, month, todayParsed.day, todayParsed.monthIndex, todayParsed.year, year]);

  const firstAvailableDayInMonth = useCallback(
    (nextYear: string, nextMonth: string): string | null => {
      const yi = Number(nextYear);
      const mi = Number(nextMonth) - 1;
      if (!Number.isFinite(yi) || !Number.isFinite(mi) || mi < 0 || mi > 11) {
        return null;
      }
      const maxDay = daysInMonth(yi, mi);
      let startDay = 1;
      if (yi === todayParsed.year && mi === todayParsed.monthIndex) {
        startDay = todayParsed.day;
      }
      for (let d = startDay; d <= maxDay; d += 1) {
        const key = toCalendarDayKey(yi, mi, d);
        const status = dayAvailability ? dayAvailability(key) : 'available';
        if (status === 'available') {
          return String(d).padStart(2, '0');
        }
      }
      return null;
    },
    [dayAvailability, todayParsed.day, todayParsed.monthIndex, todayParsed.year],
  );

  const commitParts = useCallback(
    (nextYear: string, nextMonth: string, nextDay: string) => {
      const yi = Number(nextYear);
      const mi = Number(nextMonth) - 1;
      let di = Number(nextDay);
      if (!Number.isFinite(yi) || !Number.isFinite(mi) || !Number.isFinite(di)) {
        return;
      }
      const cap = daysInMonth(yi, mi);
      if (di > cap) {
        di = cap;
      }
      let key = toCalendarDayKey(yi, mi, di);
      let status = dayAvailability ? dayAvailability(key) : isPastCalendarDayKey(key) ? 'past' : 'available';
      if (status !== 'available') {
        const fallbackDay = firstAvailableDayInMonth(nextYear, nextMonth);
        if (!fallbackDay) {
          return;
        }
        di = Number(fallbackDay);
        key = toCalendarDayKey(yi, mi, di);
        status = dayAvailability ? dayAvailability(key) : 'available';
        if (status !== 'available') {
          return;
        }
      }
      setYear(String(yi));
      setMonth(String(mi + 1).padStart(2, '0'));
      setDay(String(di).padStart(2, '0'));
      onDayKeyChange(key);
    },
    [dayAvailability, firstAvailableDayInMonth, onDayKeyChange],
  );

  const onYearChange = (nextYear: string) => {
    let nextMonth = month;
    let nextDay = day;
    const yi = Number(nextYear);
    if (yi === todayParsed.year) {
      const minMonth = todayParsed.monthIndex + 1;
      if (Number(nextMonth) < minMonth) {
        nextMonth = String(minMonth).padStart(2, '0');
      }
      if (yi === todayParsed.year && Number(nextMonth) === minMonth) {
        const minDay = todayParsed.day;
        if (Number(nextDay) < minDay) {
          nextDay = String(minDay).padStart(2, '0');
        }
      }
    }
    commitParts(nextYear, nextMonth, nextDay);
  };

  const onMonthChange = (nextMonth: string) => {
    let nextDay = day;
    const yi = Number(year);
    const mi = Number(nextMonth) - 1;
    if (yi === todayParsed.year && mi === todayParsed.monthIndex && Number(nextDay) < todayParsed.day) {
      nextDay = String(todayParsed.day).padStart(2, '0');
    }
    commitParts(year, nextMonth, nextDay);
  };

  const onDayChange = (nextDay: string) => {
    commitParts(year, month, nextDay);
  };

  const monthLabel = new Intl.DateTimeFormat(language === 'he' ? 'he-IL' : undefined, {
    month: 'long',
    year: 'numeric',
  }).format(new Date(viewYear, viewMonthIndex, 1));

  const weekdayLabels = Array.from({ length: 7 }, (_, i) =>
    new Intl.DateTimeFormat(language === 'he' ? 'he-IL' : undefined, { weekday: 'short' }).format(
      new Date(2024, 0, 7 + i),
    ),
  );

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <span className={styles.label} id={`${popoverId}-legend`}>
        {label}
      </span>
      <div className={styles.pickerRow} role="group" aria-labelledby={`${popoverId}-legend`}>
        <div className={styles.scrollColumns}>
          <div className={styles.scrollColumn}>
            <span className={styles.columnLabel}>{dayColumnLabel}</span>
            <ScrollWheelColumn
              id={`${popoverId}-day`}
              items={dayItems}
              value={day}
              ariaLabel={dayColumnLabel}
              disabled={disabled || dayItems.length === 0}
              onChange={onDayChange}
            />
          </div>
          <div className={styles.scrollColumn}>
            <span className={styles.columnLabel}>{monthColumnLabel}</span>
            <ScrollWheelColumn
              id={`${popoverId}-month`}
              items={monthItems}
              value={month}
              ariaLabel={monthColumnLabel}
              disabled={disabled || monthItems.length === 0}
              onChange={onMonthChange}
            />
          </div>
          <div className={styles.scrollColumn}>
            <span className={styles.columnLabel}>{yearColumnLabel}</span>
            <ScrollWheelColumn
              id={`${popoverId}-year`}
              items={yearItems}
              value={year}
              ariaLabel={yearColumnLabel}
              disabled={disabled || yearItems.length === 0}
              onChange={onYearChange}
            />
          </div>
        </div>
        <button
          type="button"
          className={styles.calendarBtn}
          disabled={disabled}
          aria-label={openCalendarLabel}
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
            <rect x="3" y="5" width="18" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
            <path d="M8 3v4M16 3v4M3 10h18" stroke="currentColor" strokeWidth="1.6" />
          </svg>
        </button>
      </div>
      {open ? (
        <div className={styles.popover} role="dialog" aria-label={openCalendarLabel}>
          <div className={styles.popoverHead}>
            <button
              type="button"
              className={styles.navBtn}
              aria-label={prevMonthLabel}
              onClick={() => {
                const next = shiftMonth(viewYear, viewMonthIndex, -1);
                setViewYear(next.year);
                setViewMonthIndex(next.monthIndex);
              }}
            >
              ‹
            </button>
            <span className={styles.monthLabel}>{monthLabel}</span>
            <button
              type="button"
              className={styles.navBtn}
              aria-label={nextMonthLabel}
              onClick={() => {
                const next = shiftMonth(viewYear, viewMonthIndex, 1);
                setViewYear(next.year);
                setViewMonthIndex(next.monthIndex);
              }}
            >
              ›
            </button>
          </div>
          <div className={styles.weekdays}>
            {weekdayLabels.map((name) => (
              <span key={name} className={styles.weekday}>
                {name}
              </span>
            ))}
          </div>
          <div className={styles.grid}>
            {buildMonthCalendarGrid(viewYear, viewMonthIndex).map((cell) => {
              const status = dayAvailability
                ? dayAvailability(cell.dayKey)
                : isPastCalendarDayKey(cell.dayKey)
                  ? 'past'
                  : 'available';
              const selected = dayKey === cell.dayKey;
              const blocked = status !== 'available' || !cell.inMonth;
              return (
                <button
                  key={cell.dayKey}
                  type="button"
                  className={[
                    styles.dayBtn,
                    !cell.inMonth ? styles.dayBtnOutside : '',
                    status === 'closed' ? styles.dayBtnClosed : '',
                    status === 'fullyBooked' ? styles.dayBtnFullyBooked : '',
                    selected ? styles.dayBtnSelected : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  disabled={blocked}
                  onClick={() => {
                    onDayKeyChange(cell.dayKey);
                    setOpen(false);
                  }}
                >
                  {cell.day}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
